import { useRef, useState } from "react";
import { toast } from "sonner";
import { FileSpreadsheet, Loader2, Upload, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { uploadFileOffline } from "@/lib/uploadOffline";

const COLS = [
  ["carrier", "Carrier"], ["technology", "Tech"], ["band", "Band"], ["rssi", "RSSI"],
  ["rsrq", "RSRQ"], ["rsrp", "RSRP"], ["sinr", "SINR"], ["status", "Status"],
];

const statusCls = (s = "") => (s.toLowerCase().startsWith("good") ? "text-emerald-400" : s.toLowerCase().startsWith("poor") ? "text-red-400" : "text-slate-300");

const Readings = ({ rows, testid }) => (
  <div className="overflow-x-auto rounded border border-slate-800" data-testid={testid}>
    <table className="w-full text-[11px] font-mono">
      <thead className="bg-slate-800 text-slate-300">
        <tr>{COLS.map(([, h]) => <th key={h} className="px-2 py-1 text-left font-semibold">{h}</th>)}</tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} className="border-t border-slate-800">
            {COLS.map(([k]) => (
              <td key={k} className={`px-2 py-1 whitespace-nowrap ${k === "status" ? statusCls(r.status) : "text-slate-200"}`}>{r[k] || "—"}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Slot = ({ project, orientation, data, onUploaded }) => {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const tid = `wilson-${orientation}`;
  const title = orientation === "horizontal" ? "Horizontal" : "Vertical";

  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const r = await uploadFileOffline({
        projectId: project.id,
        file,
        label: `Wilson ${title}`,
        fields: { kind: "project_doc", category: "Wilson Pro Signal Tests", requirement_key: `wilson_${orientation}` },
      });
      toast[r.queued ? "info" : "success"](r.queued ? `${title} CSV saved on this device — uploads when back online` : `${title} CSV uploaded`);
      if (!r.queued) onUploaded();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-md border border-slate-800 bg-slate-950 p-3 space-y-2" data-testid={tid}>
      <div className="flex flex-wrap items-center gap-2">
        <FileSpreadsheet className="w-4 h-4 text-sky-300" aria-hidden="true" />
        <div className="font-medium text-sm text-slate-100">Antenna {title}</div>
        {data ? (
          <span className="inline-flex items-center gap-1 text-[11px] text-emerald-400" data-testid={`${tid}-status`}>
            <CheckCircle2 className="w-3.5 h-3.5" /> {data.filename}
          </span>
        ) : (
          <span className="text-[11px] text-slate-500" data-testid={`${tid}-status`}>Not uploaded — CandidateID_{title}.csv</span>
        )}
        <input ref={input} type="file" accept=".csv,text/csv" className="hidden" onChange={pick} data-testid={`${tid}-input`} />
        <Button size="sm" variant="outline" disabled={busy} onClick={() => input.current?.click()} className="ml-auto border-slate-700" data-testid={`${tid}-upload`}>
          {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1" />}
          {data ? "Replace" : "Upload CSV"}
        </Button>
      </div>
      {data?.rows?.length > 0 && <Readings rows={data.rows} testid={`${tid}-table`} />}
      {data && !data.rows?.length && (
        <div className="text-[11px] text-amber-300" data-testid={`${tid}-error`}>{data.error || "No readings found in this file"} — the file is still saved.</div>
      )}
    </div>
  );
};

// Wilson Pro CSV uploads (SOP: CandidateID_Horizontal.csv + CandidateID_Vertical.csv).
export default function WilsonUploads({ project, wilson = {}, onUploaded }) {
  return (
    <div className="space-y-2" data-testid="wilson-uploads">
      <div className="text-xs text-slate-400">
        Upload both Wilson Pro exports from CellLinq. Once both are in, the question above is marked Complete.
      </div>
      {["horizontal", "vertical"].map((o) => (
        <Slot key={o} project={project} orientation={o} data={wilson[o]} onUploaded={onUploaded} />
      ))}
    </div>
  );
}
